import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API, getAuthHeaders, handleApiError } from '../../App';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Card } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Plus, TrendingUp, TrendingDown, ShoppingCart, Banknote, Smartphone, 
  Calendar, ChevronDown, Check, Search, ChevronLeft, ChevronRight, 
  AlertCircle, FileText, UserPlus, Phone, RotateCcw, ArrowDownLeft, Printer, BarChart3,
  Pencil, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { usePrintShortcut, generatePrintContent, openPrintWindow, formatPrintCurrency, formatPrintDate } from '../../utils/printUtils';
import PLStatementDialog from '../../components/pharmacy/PLStatementDialog';

// Distributor Dropdown Component
const DistributorDropdown = ({ distributors, value, onChange, onAddNew, placeholder = "Select Distributor" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);
  
  const filteredDistributors = distributors.filter(d => 
    d.name.toLowerCase().includes(search.toLowerCase())
  );

  const selectedDistributor = distributors.find(d => d.id === value);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full h-10 px-3 flex items-center justify-between border border-slate-200 rounded-lg bg-white hover:border-slate-300 transition-colors text-left text-sm"
      >
        <span className={selectedDistributor ? 'text-slate-900' : 'text-slate-400'}>
          {selectedDistributor ? selectedDistributor.name : placeholder}
        </span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-hidden">
          <button
            type="button"
            onClick={() => { setIsOpen(false); onAddNew(); }}
            className="w-full px-3 py-2.5 flex items-center gap-2 text-green-600 hover:bg-green-50 border-b border-slate-100"
          >
            <UserPlus className="h-4 w-4" />
            <span className="text-sm font-medium">+ Add New Party</span>
          </button>
          
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search distributors..."
                className="pl-8 h-9 text-sm"
              />
            </div>
          </div>
          
          <div className="max-h-48 overflow-y-auto">
            {filteredDistributors.length === 0 ? (
              <div className="px-3 py-4 text-sm text-slate-500 text-center">No distributors found</div>
            ) : (
              filteredDistributors.map(d => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => { onChange(d.id, d.name); setIsOpen(false); setSearch(''); }}
                  className={`w-full px-3 py-2.5 text-left text-sm hover:bg-slate-50 flex items-center justify-between ${value === d.id ? 'bg-blue-50 text-blue-700' : ''}`}
                >
                  <span>{d.name}</span>
                  {value === d.id && <Check className="h-4 w-4" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const SalePurchase = () => {
  const [activeTab, setActiveTab] = useState('sales');
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [showPL, setShowPL] = useState(false);
  
  // Data states
  const [sales, setSales] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [purchaseReturns, setPurchaseReturns] = useState([]);
  const [distributors, setDistributors] = useState([]);
  
  // Summary states
  const [salesSummary, setSalesSummary] = useState({ total_sales: 0, total_cash_sales: 0, total_paytm_sales: 0, entries_count: 0 });
  const [purchaseSummary, setPurchaseSummary] = useState({ total_purchases: 0, total_returns: 0, net_purchases: 0 });
  
  // Dialog states
  const [showAddSale, setShowAddSale] = useState(false);
  const [showAddPurchase, setShowAddPurchase] = useState(false);
  const [showAddReturn, setShowAddReturn] = useState(false);
  const [showAddParty, setShowAddParty] = useState(false);
  
  // Edit states
  const [editingSale, setEditingSale] = useState(null);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [showConfirmEdit, setShowConfirmEdit] = useState(false);
  const [pendingEditData, setPendingEditData] = useState(null);
  const [editType, setEditType] = useState(null); // 'sale' or 'purchase'

  // Duplicate-bill error popup state
  const [duplicateError, setDuplicateError] = useState(null); // { message, existing }
  
  // Form states
  const [newSale, setNewSale] = useState({
    cash_sale: '',
    paytm_sale: '',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  
  const [newPurchase, setNewPurchase] = useState({
    distributor_id: '',
    distributor_name: '',
    bill_number: '',
    bill_amount: '',
    date: new Date().toISOString().split('T')[0]
  });
  
  const [newReturn, setNewReturn] = useState({
    distributor_id: '',
    distributor_name: '',
    bill_number: '',
    return_amount: '',
    date: new Date().toISOString().split('T')[0],
    reason: ''
  });
  
  const [newParty, setNewParty] = useState({ name: '', phone: '' });
  const [addingParty, setAddingParty] = useState(false);
  const [distributorOutstanding, setDistributorOutstanding] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/parties/seed`, {}, { headers: getAuthHeaders() }).catch(() => {});
      
      const [salesRes, purchasesRes, returnsRes, partiesRes, salesSumRes, purchaseSumRes] = await Promise.all([
        axios.get(`${API}/sales?firm=pharmacy&month=${selectedMonth}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/purchases?month=${selectedMonth}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/purchase-returns?month=${selectedMonth}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/parties`, { headers: getAuthHeaders() }),
        axios.get(`${API}/sales/summary?firm=pharmacy&month=${selectedMonth}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/purchases/summary?month=${selectedMonth}`, { headers: getAuthHeaders() })
      ]);
      
      setSales(salesRes.data);
      setPurchases(purchasesRes.data);
      setPurchaseReturns(returnsRes.data);
      setDistributors(partiesRes.data.filter(p => p.type === 'distributor'));
      setSalesSummary(salesSumRes.data);
      setPurchaseSummary(purchaseSumRes.data);
    } catch (error) {
      handleApiError(error, 'Failed to fetch data');
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch distributor outstanding when selected
  const fetchDistributorOutstanding = async (distributorId) => {
    try {
      const res = await axios.get(`${API}/distributor-outstanding/${distributorId}`, { headers: getAuthHeaders() });
      setDistributorOutstanding(res.data.outstanding_balance);
    } catch (error) {
      setDistributorOutstanding(0);
    }
  };

  // Reset form functions
  const resetSaleForm = () => {
    setNewSale({ cash_sale: '', paytm_sale: '', date: new Date().toISOString().split('T')[0], notes: '' });
    setEditingSale(null);
  };

  const resetPurchaseForm = () => {
    setNewPurchase({ distributor_id: '', distributor_name: '', bill_number: '', bill_amount: '', date: new Date().toISOString().split('T')[0] });
    setEditingPurchase(null);
    setDistributorOutstanding(0);
  };

  // Edit Sale handler
  const handleEditSale = (sale) => {
    setEditingSale(sale);
    setNewSale({
      cash_sale: sale.cash_sale?.toString() || '',
      paytm_sale: sale.paytm_sale?.toString() || '',
      date: sale.date,
      notes: sale.notes || ''
    });
    setShowAddSale(true);
  };

  // Edit Purchase handler
  const handleEditPurchase = async (purchase) => {
    setEditingPurchase(purchase);
    setNewPurchase({
      distributor_id: purchase.distributor_id,
      distributor_name: purchase.distributor_name,
      bill_number: purchase.bill_number,
      bill_amount: purchase.bill_amount?.toString() || '',
      date: purchase.date
    });
    if (purchase.distributor_id) {
      await fetchDistributorOutstanding(purchase.distributor_id);
    }
    setShowAddPurchase(true);
  };

  // Confirm Edit handler
  const confirmEdit = async () => {
    if (!pendingEditData) return;
    
    try {
      if (editType === 'sale') {
        await axios.put(`${API}/sales/${editingSale.id}`, pendingEditData, { headers: getAuthHeaders() });
        toast.success('Sale updated successfully');
        setShowAddSale(false);
        resetSaleForm();
      } else if (editType === 'purchase') {
        await axios.put(`${API}/purchases/${editingPurchase.id}`, pendingEditData, { headers: getAuthHeaders() });
        toast.success('Purchase updated & ledger synced');
        setShowAddPurchase(false);
        resetPurchaseForm();
      }
      setShowConfirmEdit(false);
      setPendingEditData(null);
      setEditType(null);
      fetchData();
    } catch (error) {
      if (error.response?.status === 409) {
        const detail = error.response.data?.detail;
        setDuplicateError(typeof detail === 'object' ? detail : { message: String(detail) });
        setShowConfirmEdit(false);
        return;
      }
      toast.error(`Failed to update ${editType}`);
    }
  };

  // Handle Add/Edit Sale
  const handleAddSale = async (e) => {
    e.preventDefault();
    const cashSale = parseFloat(newSale.cash_sale) || 0;
    const paytmSale = parseFloat(newSale.paytm_sale) || 0;
    const totalSale = cashSale + paytmSale;
    
    if (totalSale <= 0) {
      toast.error('Please enter at least one sale amount');
      return;
    }

    const saleData = {
      firm: 'pharmacy',
      cash_sale: cashSale,
      paytm_sale: paytmSale,
      total_sale: totalSale,
      date: newSale.date,
      notes: newSale.notes
    };

    // If editing, show confirmation dialog
    if (editingSale) {
      setPendingEditData(saleData);
      setEditType('sale');
      setShowConfirmEdit(true);
      return;
    }
    
    // Add new sale
    try {
      await axios.post(`${API}/sales`, saleData, { headers: getAuthHeaders() });
      
      toast.success('Sale added successfully');
      setShowAddSale(false);
      resetSaleForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to add sale');
    }
  };

  // Handle Add/Edit Purchase
  const handleAddPurchase = async (e) => {
    e.preventDefault();
    if (!newPurchase.distributor_id) {
      toast.error('Please select a distributor');
      return;
    }
    
    const billAmount = parseFloat(newPurchase.bill_amount) || 0;
    if (billAmount <= 0) {
      toast.error('Please enter a valid bill amount');
      return;
    }

    const purchaseData = {
      ...newPurchase,
      bill_amount: billAmount,
      previous_outstanding: distributorOutstanding,
      new_outstanding: distributorOutstanding + billAmount
    };

    // If editing, show confirmation dialog
    if (editingPurchase) {
      setPendingEditData(purchaseData);
      setEditType('purchase');
      setShowConfirmEdit(true);
      return;
    }
    
    // Add new purchase
    try {
      await axios.post(`${API}/purchases`, purchaseData, { headers: getAuthHeaders() });
      
      toast.success('Purchase added & synced to Khatabook');
      setShowAddPurchase(false);
      resetPurchaseForm();
      fetchData();
    } catch (error) {
      if (error.response?.status === 409) {
        const detail = error.response.data?.detail;
        setDuplicateError(typeof detail === 'object' ? detail : { message: String(detail) });
        return;
      }
      toast.error('Failed to add purchase');
    }
  };

  // Handle Add Purchase Return
  const handleAddReturn = async (e) => {
    e.preventDefault();
    if (!newReturn.distributor_id) {
      toast.error('Please select a distributor');
      return;
    }
    
    const returnAmount = parseFloat(newReturn.return_amount) || 0;
    if (returnAmount <= 0) {
      toast.error('Please enter a valid return amount');
      return;
    }
    
    try {
      await axios.post(`${API}/purchase-returns`, newReturn, { headers: getAuthHeaders() });
      
      toast.success('Purchase return added & synced to Khatabook');
      setShowAddReturn(false);
      setNewReturn({ distributor_id: '', distributor_name: '', bill_number: '', return_amount: '', date: new Date().toISOString().split('T')[0], reason: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add purchase return');
    }
  };

  // Handle Add New Party
  const handleAddNewParty = async (e) => {
    e.preventDefault();
    if (!newParty.name.trim()) {
      toast.error('Party name is required');
      return;
    }
    
    setAddingParty(true);
    try {
      const res = await axios.post(`${API}/parties`, {
        name: newParty.name.trim(),
        type: 'distributor',
        phone: newParty.phone || null
      }, { headers: getAuthHeaders() });
      
      const newDistributor = res.data;
      setDistributors([...distributors, newDistributor]);
      
      setNewPurchase({ ...newPurchase, distributor_id: newDistributor.id, distributor_name: newDistributor.name });
      setDistributorOutstanding(0);
      
      toast.success(`"${newDistributor.name}" added`);
      setShowAddParty(false);
      setNewParty({ name: '', phone: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add party');
    } finally {
      setAddingParty(false);
    }
  };

  // Print function for Sale/Purchase
  const handlePrint = useCallback(() => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const [year, month] = selectedMonth.split('-').map(Number);
    const dateRange = `${months[month - 1]} ${year}`;

    let headers, rows;
    
    if (activeTab === 'sales') {
      headers = [
        { label: 'Date', align: 'left' },
        { label: 'Cash Sale', align: 'right' },
        { label: 'Paytm Sale', align: 'right' },
        { label: 'Total Sale', align: 'right' },
        { label: 'Notes', align: 'left' }
      ];
      rows = sales.map(s => [
        formatPrintDate(s.date),
        `<span class="amount-positive">${formatPrintCurrency(s.cash_sale)}</span>`,
        `<span class="badge badge-purple">${formatPrintCurrency(s.paytm_sale)}</span>`,
        `<span class="amount-neutral">${formatPrintCurrency(s.total_sale)}</span>`,
        s.notes || '-'
      ]);
    } else {
      headers = [
        { label: 'Date', align: 'left' },
        { label: 'Type', align: 'center' },
        { label: 'Party Name', align: 'left' },
        { label: 'Bill No.', align: 'left' },
        { label: 'Amount', align: 'right' }
      ];
      const allPurchases = [
        ...purchases.map(p => ({ ...p, type: 'Purchase', amount: p.bill_amount })),
        ...purchaseReturns.map(r => ({ ...r, type: 'Return', amount: -r.return_amount, bill_number: r.bill_number }))
      ].sort((a, b) => new Date(b.date) - new Date(a.date));

      rows = allPurchases.map(p => [
        formatPrintDate(p.date),
        `<span class="badge ${p.type === 'Purchase' ? 'badge-blue' : 'badge-red'}">${p.type}</span>`,
        p.distributor_name || '-',
        p.bill_number || '-',
        p.type === 'Return' 
          ? `<span class="amount-negative">-${formatPrintCurrency(Math.abs(p.amount))}</span>`
          : `<span class="amount-neutral">${formatPrintCurrency(p.amount)}</span>`
      ]);
    }

    const summary = activeTab === 'sales' ? [
      { label: 'Cash Sales', value: formatPrintCurrency(salesSummary.total_cash_sales), type: 'positive' },
      { label: 'Paytm Sales', value: formatPrintCurrency(salesSummary.total_paytm_sales), type: 'neutral' },
      { label: 'Total Sales', value: formatPrintCurrency(salesSummary.total_sales), type: 'positive' }
    ] : [
      { label: 'Total Purchases', value: formatPrintCurrency(purchaseSummary.total_purchases), type: 'neutral' },
      { label: 'Returns', value: formatPrintCurrency(purchaseSummary.total_returns), type: 'negative' },
      { label: 'Net Purchase', value: formatPrintCurrency(purchaseSummary.net_purchases), type: 'neutral' }
    ];

    const content = generatePrintContent({
      title: activeTab === 'sales' ? 'Sales Report' : 'Purchase Report',
      businessName: 'Shri Kripa Pharmacy',
      dateRange,
      headers,
      rows,
      summary
    });

    openPrintWindow(content);
    toast.success('Opening print preview...');
  }, [activeTab, sales, purchases, purchaseReturns, salesSummary, purchaseSummary, selectedMonth]);

  // Register Alt+P shortcut
  usePrintShortcut(handlePrint);

  // Month navigation
  const changeMonth = (direction) => {
    const date = new Date(selectedMonth + '-01');
    date.setMonth(date.getMonth() + direction);
    setSelectedMonth(date.toISOString().slice(0, 7));
  };

  const formatMonth = (monthStr) => {
    const date = new Date(monthStr + '-01');
    return date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-96"><div className="text-slate-500">Loading...</div></div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-4 max-w-6xl mx-auto" data-testid="sale-purchase-container">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Sale / Purchase</h1>
            <p className="text-sm text-slate-500">Track sales and manage purchases</p>
          </div>
          
          {/* Month Selector */}
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setShowPL(true)}
              className="h-9 bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="open-pl-statement-btn"
              title="Profit & Loss Statement"
            >
              <BarChart3 className="h-4 w-4 mr-1.5" />P&amp;L Statement
            </Button>
            <Button onClick={handlePrint} variant="outline" className="h-9" title="Print (Alt+P)" data-testid="print-sale-purchase-btn">
              <Printer className="h-4 w-4 mr-1.5" />Print
            </Button>
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-2 py-1">
              <button onClick={() => changeMonth(-1)} className="p-1 hover:bg-slate-100 rounded">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="flex items-center gap-1.5 px-2">
                <Calendar className="h-4 w-4 text-slate-500" />
                <span className="text-sm font-medium min-w-[120px] text-center">{formatMonth(selectedMonth)}</span>
              </div>
              <button onClick={() => changeMonth(1)} className="p-1 hover:bg-slate-100 rounded">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Monthly Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {/* Sales Summary */}
          <Card className="p-3 border-green-200 bg-green-50/50">
            <div className="flex items-center gap-2 mb-1">
              <Banknote className="h-4 w-4 text-green-600" />
              <span className="text-xs font-medium text-green-600 uppercase">Cash Sales</span>
            </div>
            <p className="text-lg font-semibold text-green-700">{formatCurrency(salesSummary.total_cash_sales)}</p>
          </Card>
          
          <Card className="p-3 border-purple-200 bg-purple-50/50">
            <div className="flex items-center gap-2 mb-1">
              <Smartphone className="h-4 w-4 text-purple-600" />
              <span className="text-xs font-medium text-purple-600 uppercase">Paytm Sales</span>
            </div>
            <p className="text-lg font-semibold text-purple-700">{formatCurrency(salesSummary.total_paytm_sales)}</p>
          </Card>
          
          <Card className="p-3 border-blue-200 bg-blue-50/50">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-4 w-4 text-blue-600" />
              <span className="text-xs font-medium text-blue-600 uppercase">Total Sales</span>
            </div>
            <p className="text-lg font-semibold text-blue-700">{formatCurrency(salesSummary.total_sales)}</p>
          </Card>
          
          {/* Purchase Summary */}
          <Card className="p-3 border-orange-200 bg-orange-50/50">
            <div className="flex items-center gap-2 mb-1">
              <ShoppingCart className="h-4 w-4 text-orange-600" />
              <span className="text-xs font-medium text-orange-600 uppercase">Purchases</span>
            </div>
            <p className="text-lg font-semibold text-orange-700">{formatCurrency(purchaseSummary.total_purchases)}</p>
          </Card>
          
          <Card className="p-3 border-red-200 bg-red-50/50">
            <div className="flex items-center gap-2 mb-1">
              <ArrowDownLeft className="h-4 w-4 text-red-600" />
              <span className="text-xs font-medium text-red-600 uppercase">Returns</span>
            </div>
            <p className="text-lg font-semibold text-red-700">{formatCurrency(purchaseSummary.total_returns)}</p>
          </Card>
          
          <Card className="p-3 border-slate-200 bg-slate-50/50">
            <div className="flex items-center gap-2 mb-1">
              <TrendingDown className="h-4 w-4 text-slate-600" />
              <span className="text-xs font-medium text-slate-600 uppercase">Net Purchase</span>
            </div>
            <p className="text-lg font-semibold text-slate-700">{formatCurrency(purchaseSummary.net_purchases)}</p>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <div className="flex items-center justify-between">
            <TabsList className="grid grid-cols-2 w-64">
              <TabsTrigger value="sales" className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />Sales
              </TabsTrigger>
              <TabsTrigger value="purchases" className="flex items-center gap-2">
                <ShoppingCart className="h-4 w-4" />Purchases
              </TabsTrigger>
            </TabsList>
            
            {activeTab === 'sales' && (
              <Button onClick={() => setShowAddSale(true)} className="bg-green-600 hover:bg-green-700">
                <Plus className="h-4 w-4 mr-1" />Add Sale
              </Button>
            )}
            {activeTab === 'purchases' && (
              <div className="flex gap-2">
                <Button onClick={() => setShowAddReturn(true)} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50">
                  <RotateCcw className="h-4 w-4 mr-1" />Add Return
                </Button>
                <Button onClick={() => setShowAddPurchase(true)} className="bg-orange-600 hover:bg-orange-700">
                  <Plus className="h-4 w-4 mr-1" />Add Purchase
                </Button>
              </div>
            )}
          </div>

          {/* Sales Tab */}
          <TabsContent value="sales" className="space-y-4">
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Date</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Cash Sale</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Paytm Sale</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Total Sale</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Notes</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase w-16">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sales.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                          No sales recorded for {formatMonth(selectedMonth)}
                        </td>
                      </tr>
                    ) : (
                      sales.map(sale => (
                        <tr key={sale.id} className="hover:bg-slate-50" data-testid={`sale-row-${sale.id}`}>
                          <td className="px-4 py-3 text-sm font-medium text-slate-900">{formatDate(sale.date)}</td>
                          <td className="px-4 py-3 text-sm text-right text-green-600 font-medium">{formatCurrency(sale.cash_sale)}</td>
                          <td className="px-4 py-3 text-sm text-right text-purple-600 font-medium">{formatCurrency(sale.paytm_sale)}</td>
                          <td className="px-4 py-3 text-sm text-right text-blue-600 font-semibold">{formatCurrency(sale.total_sale)}</td>
                          <td className="px-4 py-3 text-sm text-slate-500">{sale.notes || '-'}</td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handleEditSale(sale)}
                              className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Edit sale"
                              data-testid={`edit-sale-${sale.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* Purchases Tab */}
          <TabsContent value="purchases" className="space-y-4">
            {/* Purchases Table */}
            <Card className="overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                <h3 className="font-semibold text-slate-700">Purchases</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Date</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Party Name</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Bill No.</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Amount</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase w-16">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {purchases.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                          No purchases recorded for {formatMonth(selectedMonth)}
                        </td>
                      </tr>
                    ) : (
                      purchases.map(p => (
                        <tr key={p.id} className="hover:bg-slate-50" data-testid={`purchase-row-${p.id}`}>
                          <td className="px-4 py-3 text-sm text-slate-900">{formatDate(p.date)}</td>
                          <td className="px-4 py-3 text-sm font-medium text-slate-900">{p.distributor_name}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{p.bill_number}</td>
                          <td className="px-4 py-3 text-sm text-right text-orange-600 font-semibold">{formatCurrency(p.bill_amount)}</td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handleEditPurchase(p)}
                              className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Edit purchase"
                              data-testid={`edit-purchase-${p.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
            
            {/* Purchase Returns Table */}
            {purchaseReturns.length > 0 && (
              <Card className="overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 bg-red-50">
                  <h3 className="font-semibold text-red-700">Purchase Returns</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Date</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Party Name</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Bill Ref.</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Reason</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {purchaseReturns.map(r => (
                        <tr key={r.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm text-slate-900">{formatDate(r.date)}</td>
                          <td className="px-4 py-3 text-sm font-medium text-slate-900">{r.distributor_name}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{r.bill_number}</td>
                          <td className="px-4 py-3 text-sm text-slate-500">{r.reason || '-'}</td>
                          <td className="px-4 py-3 text-sm text-right text-red-600 font-semibold">-{formatCurrency(r.return_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Add Sale Dialog */}
        <Dialog open={showAddSale} onOpenChange={(open) => { setShowAddSale(open); if (!open) resetSaleForm(); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingSale ? 'Edit Sale Entry' : 'Add Sale Entry'}</DialogTitle>
              <DialogDescription>{editingSale ? 'Modify sale record' : 'Record today\'s sales (Cash + Paytm)'}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddSale} className="space-y-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={newSale.date}
                  onChange={(e) => setNewSale({ ...newSale, date: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Banknote className="h-4 w-4 text-green-600" />Cash Sale (₹)
                  </Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={newSale.cash_sale}
                    onChange={(e) => setNewSale({ ...newSale, cash_sale: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Smartphone className="h-4 w-4 text-purple-600" />Paytm Sale (₹)
                  </Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={newSale.paytm_sale}
                    onChange={(e) => setNewSale({ ...newSale, paytm_sale: e.target.value })}
                  />
                </div>
              </div>
              {(newSale.cash_sale || newSale.paytm_sale) && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-600">
                    <span className="font-medium">Total Sale:</span>{' '}
                    {formatCurrency((parseFloat(newSale.cash_sale) || 0) + (parseFloat(newSale.paytm_sale) || 0))}
                  </p>
                </div>
              )}
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Input
                  placeholder="Any notes..."
                  value={newSale.notes}
                  onChange={(e) => setNewSale({ ...newSale, notes: e.target.value })}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setShowAddSale(false); resetSaleForm(); }}>Cancel</Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700">{editingSale ? 'Save Changes' : 'Add Sale'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add Purchase Dialog */}
        <Dialog open={showAddPurchase} onOpenChange={(open) => { setShowAddPurchase(open); if (!open) resetPurchaseForm(); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingPurchase ? 'Edit Purchase' : 'Add Purchase'}</DialogTitle>
              <DialogDescription>{editingPurchase ? 'Modify purchase record (will update ledger)' : 'Purchase will be synced to Khatabook'}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddPurchase} className="space-y-4">
              <div className="space-y-2">
                <Label>Distributor / Party</Label>
                <DistributorDropdown
                  distributors={distributors}
                  value={newPurchase.distributor_id}
                  onChange={(id, name) => {
                    setNewPurchase({ ...newPurchase, distributor_id: id, distributor_name: name });
                    fetchDistributorOutstanding(id);
                  }}
                  onAddNew={() => setShowAddParty(true)}
                />
              </div>
              
              {newPurchase.distributor_id && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-700">
                    <span className="font-medium">Current Outstanding:</span> {formatCurrency(distributorOutstanding)}
                  </p>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Bill Number</Label>
                <Input
                  placeholder="e.g., INV-001"
                  value={newPurchase.bill_number}
                  onChange={(e) => setNewPurchase({ ...newPurchase, bill_number: e.target.value })}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Bill Amount (₹)</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={newPurchase.bill_amount}
                  onChange={(e) => setNewPurchase({ ...newPurchase, bill_amount: e.target.value })}
                  required
                />
              </div>
              
              {newPurchase.bill_amount && newPurchase.distributor_id && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm text-orange-700">
                    <span className="font-medium">New Outstanding:</span>{' '}
                    {formatCurrency(distributorOutstanding + (parseFloat(newPurchase.bill_amount) || 0))}
                  </p>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={newPurchase.date}
                  onChange={(e) => setNewPurchase({ ...newPurchase, date: e.target.value })}
                  required
                />
              </div>
              
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setShowAddPurchase(false); resetPurchaseForm(); }}>Cancel</Button>
                <Button type="submit" className="bg-orange-600 hover:bg-orange-700">{editingPurchase ? 'Save Changes' : 'Add Purchase'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Confirmation Dialog for Edit */}
        <Dialog open={showConfirmEdit} onOpenChange={setShowConfirmEdit}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Confirm Modification
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to modify this entry?
              </DialogDescription>
            </DialogHeader>
            <div className="p-4 bg-slate-50 rounded-lg text-sm text-slate-600 space-y-1">
              {editType === 'sale' && pendingEditData && (
                <>
                  <p><strong>Cash Sale:</strong> {formatCurrency(pendingEditData.cash_sale)}</p>
                  <p><strong>Paytm Sale:</strong> {formatCurrency(pendingEditData.paytm_sale)}</p>
                  <p><strong>Total:</strong> {formatCurrency(pendingEditData.total_sale)}</p>
                  <p><strong>Date:</strong> {pendingEditData.date}</p>
                </>
              )}
              {editType === 'purchase' && pendingEditData && (
                <>
                  <p><strong>Party:</strong> {pendingEditData.distributor_name}</p>
                  <p><strong>Bill No.:</strong> {pendingEditData.bill_number}</p>
                  <p><strong>Amount:</strong> {formatCurrency(pendingEditData.bill_amount)}</p>
                  <p><strong>Date:</strong> {pendingEditData.date}</p>
                </>
              )}
            </div>
            {editType === 'purchase' && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                <strong>Note:</strong> This will update the Khatabook ledger and recalculate outstanding balance.
              </div>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <Button 
                variant="outline" 
                onClick={() => { setShowConfirmEdit(false); setPendingEditData(null); setEditType(null); }}
              >
                No, Cancel
              </Button>
              <Button 
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={confirmEdit}
              >
                Yes, Confirm
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Duplicate Bill Error Dialog */}
        <Dialog open={!!duplicateError} onOpenChange={(open) => { if (!open) setDuplicateError(null); }}>
          <DialogContent className="sm:max-w-md" data-testid="duplicate-bill-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                Duplicate Bill Number
              </DialogTitle>
              <DialogDescription>
                {duplicateError?.message || 'This Bill Number already exists for this party. Duplicate entry is not allowed.'}
              </DialogDescription>
            </DialogHeader>
            {duplicateError?.existing && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-900 space-y-1">
                <div className="text-xs uppercase tracking-wide text-red-600 font-semibold mb-2">Existing Entry</div>
                <p><strong>Party:</strong> {duplicateError.existing.distributor_name || '—'}</p>
                <p><strong>Bill No.:</strong> {duplicateError.existing.bill_number || '—'}</p>
                <p><strong>Date:</strong> {duplicateError.existing.date || '—'}</p>
                <p><strong>Amount:</strong> {formatCurrency(duplicateError.existing.bill_amount || 0)}</p>
              </div>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => setDuplicateError(null)}
                data-testid="duplicate-bill-close-btn"
              >
                OK, Got it
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        <Dialog open={showAddReturn} onOpenChange={setShowAddReturn}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Purchase Return</DialogTitle>
              <DialogDescription>Return will reduce outstanding in Khatabook</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddReturn} className="space-y-4">
              <div className="space-y-2">
                <Label>Distributor / Party</Label>
                <DistributorDropdown
                  distributors={distributors}
                  value={newReturn.distributor_id}
                  onChange={(id, name) => setNewReturn({ ...newReturn, distributor_id: id, distributor_name: name })}
                  onAddNew={() => setShowAddParty(true)}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Original Bill Number</Label>
                <Input
                  placeholder="e.g., INV-001"
                  value={newReturn.bill_number}
                  onChange={(e) => setNewReturn({ ...newReturn, bill_number: e.target.value })}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Return Amount (₹)</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={newReturn.return_amount}
                  onChange={(e) => setNewReturn({ ...newReturn, return_amount: e.target.value })}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Reason (optional)</Label>
                <Input
                  placeholder="e.g., Damaged goods"
                  value={newReturn.reason}
                  onChange={(e) => setNewReturn({ ...newReturn, reason: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={newReturn.date}
                  onChange={(e) => setNewReturn({ ...newReturn, date: e.target.value })}
                  required
                />
              </div>
              
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowAddReturn(false)}>Cancel</Button>
                <Button type="submit" className="bg-red-600 hover:bg-red-700">Add Return</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add New Party Dialog */}
        <Dialog open={showAddParty} onOpenChange={setShowAddParty}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Add New Party</DialogTitle>
              <DialogDescription>Add as distributor</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddNewParty} className="space-y-4">
              <div className="space-y-2">
                <Label>Party Name *</Label>
                <Input
                  placeholder="e.g., ABC Distributors"
                  value={newParty.name}
                  onChange={(e) => setNewParty({ ...newParty, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Mobile Number (optional)</Label>
                <Input
                  placeholder="e.g., 9876543210"
                  value={newParty.phone}
                  onChange={(e) => setNewParty({ ...newParty, phone: e.target.value })}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowAddParty(false)}>Cancel</Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700" disabled={addingParty}>
                  {addingParty ? 'Adding...' : 'Add Party'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* P&L Statement Dialog */}
        <PLStatementDialog open={showPL} onOpenChange={setShowPL} />
      </div>
    </Layout>
  );
};

export default SalePurchase;
